/**
 * Finite-state reducer for the anomaly detection pipeline UI.
 *
 * Backend states drive frontend transitions verbatim — the reducer just
 * makes sure invalid transitions don't happen and that ephemeral state
 * (errors, plots) is wiped at the right time.
 */
import {
  AnomalyParameters,
  AnomalyPlots,
  ANOMALY_DEFAULTS,
  PipelineStatus,
  StlCoverage,
} from './types';
import { log } from '@/core/debug/anomalyLogger';

export interface AnomalyState {
  status: PipelineStatus;
  uploadId: string | null;
  filename: string | null;
  columnName: string | null;
  rowCount: number;
  /** Detected time step of the loaded file, in minutes (0 until loaded). */
  timestepMin: number;
  /** Detected offset of the loaded file, in minutes (0 until loaded). */
  offsetMin: number;
  params: AnomalyParameters;
  plots: AnomalyPlots;
  /**
   * Auto-English fallback message from BE (or generated FE message for client-side
   * errors). The render layer prefers `i18n.t('backendErrors.<errorCode>')` over
   * this when a known errorCode is available — see DataProcessingPage.
   */
  errorMessage: string | null;
  /** BE error_code (e.g. 'STL_PERIOD_NOT_ALIGNED') for FE i18n via mapper. */
  errorCode: string | null;
  /** BE-supplied localized suggestion (suggestions[0]); rendered as fallback when no i18n key matches. */
  errorSuggestion: string | null;
  /** server-side filename after pipeline completes, e.g. "test2_1.csv" */
  processedCsvFilename: string | null;
  /** Per-field validation error from /validate-param (debounced). */
  paramErrors: Record<string, string>;
  /** true when error should NOT auto-dismiss (BE errors that need user action). */
  errorPersistent: boolean;
  /**
   * STL coverage summary from /start (how many segments were analysed vs
   * skipped for being shorter than 2 periods). null until STL prepares; carries
   * a backend-localized `warning` when segments were skipped.
   */
  stlCoverage: StlCoverage | null;
}

export const initialAnomalyState: AnomalyState = {
  status: 'idle',
  uploadId: null,
  filename: null,
  columnName: null,
  rowCount: 0,
  timestepMin: 0,
  offsetMin: 0,
  params: ANOMALY_DEFAULTS,
  plots: {},
  errorMessage: null,
  errorCode: null,
  errorSuggestion: null,
  errorPersistent: false,
  processedCsvFilename: null,
  paramErrors: {},
  stlCoverage: null,
};

/**
 * Structured error payload for FSM dispatches. The reducer stores:
 *   - code: BE error_code or undefined for client-side errors
 *   - suggestion: BE suggestions[0] (localized) or undefined
 *   - fallback: auto-generated English text (used when neither i18n key nor suggestion is available)
 */
export interface AnomalyErrorPayload {
  code: string | undefined;
  suggestion: string | undefined;
  fallback: string;
  persistent?: boolean;  // NEW — true for BE errors that need user action
}

export type AnomalyAction =
  | { type: 'UPLOAD_STARTED'; uploadId: string; filename: string }
  | { type: 'UPLOAD_FAILED'; error: AnomalyErrorPayload }
  | {
      type: 'LOADED';
      plots: { original: AnomalyPlots['original']; slope: AnomalyPlots['slope'] };
      columnName: string;
      rowCount: number;
      /** Detected from the initial /load; omitted on use-processed promotion. */
      timestepMin?: number;
      offsetMin?: number;
    }
  | { type: 'PARAMS_UPDATED'; params: AnomalyParameters }
  | { type: 'PARAM_ERROR'; field: string; error: string | null }
  | { type: 'START_REQUESTED'; params?: AnomalyParameters }
  | {
      type: 'AWAITING_STL';
      plots: Partial<AnomalyPlots>;
      stlCoverage?: StlCoverage | null;
    }
  | {
      type: 'APPLYING_STL';
    }
  | {
      type: 'AWAITING_LSTM';
      plots: Partial<AnomalyPlots>;
    }
  | {
      type: 'APPLYING_LSTM';
    }
  | {
      type: 'COMPLETE';
      plots: Partial<AnomalyPlots>;
      processedCsvFilename: string | null;
    }
  | { type: 'PIPELINE_ERROR'; error: AnomalyErrorPayload }
  /** Server rejected a threshold value; modal stays open for retry. */
  | { type: 'THRESHOLD_VALIDATION_FAILED'; error: AnomalyErrorPayload }
  /** User cancelled a threshold modal; revert to configuring without alarm. */
  | { type: 'CANCELLED' }
  | { type: 'USE_PROCESSED' }  // optimistic: marks intent to call /use-processed
  /** Dismiss the error banner WITHOUT discarding the loaded session. */
  | { type: 'DISMISS_ERROR' }
  | { type: 'RESET' };

/**
 * Build a state slice from an error payload. Used by UPLOAD_FAILED,
 * PIPELINE_ERROR, and THRESHOLD_VALIDATION_FAILED to keep the
 * error-related field updates in one place.
 */
function applyErrorPayload(payload: AnomalyErrorPayload): {
  errorCode: string | null;
  errorSuggestion: string | null;
  errorMessage: string;
  errorPersistent: boolean;
} {
  return {
    errorCode: payload.code ?? null,
    errorSuggestion: payload.suggestion ?? null,
    errorMessage: payload.fallback,
    errorPersistent: payload.persistent ?? false,
  };
}

function anomalyReducerInner(state: AnomalyState, action: AnomalyAction): AnomalyState {
  switch (action.type) {
    case 'UPLOAD_STARTED':
      return {
        ...state,
        status: 'uploading',
        uploadId: action.uploadId,
        filename: action.filename,
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    case 'UPLOAD_FAILED':
      return { ...state, status: 'error', ...applyErrorPayload(action.error) };
    case 'LOADED':
      // Replace plots wholesale — `use-processed` re-uses LOADED to promote
      // processed→original, so stale STL/LSTM plots from the prior cycle
      // must be cleared.
      return {
        ...state,
        status: 'loaded',
        columnName: action.columnName,
        rowCount: action.rowCount,
        // Detected on the initial /load. The use-processed promotion re-uses
        // LOADED without these, so preserve the loaded file's values then.
        timestepMin: action.timestepMin ?? state.timestepMin,
        offsetMin: action.offsetMin ?? state.offsetMin,
        plots: { original: action.plots.original, slope: action.plots.slope },
        // Preserve the processed download across promotion ("übernehmen") so the
        // download button stays usable; null on a fresh load (state was null). (#102)
        processedCsvFilename: state.processedCsvFilename,
        paramErrors: {},
        // Stale STL coverage from a prior run must not leak onto the new file.
        stlCoverage: null,
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    case 'PARAMS_UPDATED': {
      // Re-entering parameter editing from any "settled" status drops the UI
      // back into `configuring` so stepper / Start button reflect intent.
      const reconfigurable = new Set(['loaded', 'complete', 'error']);
      const isRecovering = reconfigurable.has(state.status);
      return {
        ...state,
        params: action.params,
        status: isRecovering ? 'configuring' : state.status,
        // W9-F2: when recovering from error, clear error state so the UI
        // doesn't show stale error while the user is re-configuring.
        ...(isRecovering && {
          errorMessage: null,
          errorCode: null,
          errorSuggestion: null,
          errorPersistent: false,
        }),
      };
    }
    case 'PARAM_ERROR': {
      const next = { ...state.paramErrors };
      if (action.error) next[action.field] = action.error;
      else delete next[action.field];
      return { ...state, paramErrors: next };
    }
    case 'START_REQUESTED':
      return {
        ...state,
        status: 'running',
        // Freeze the params actually used for this run into reducer state, so the
        // info file and progress derivation reflect reality (the page keeps params
        // in its own useState and otherwise never syncs them here). Falls back to
        // existing params when a caller omits them (e.g. legacy dispatches/tests).
        params: action.params ?? state.params,
        // New run — drop any coverage banner from the previous STL pass.
        stlCoverage: null,
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    case 'AWAITING_STL':
      return {
        ...state,
        status: 'awaiting_stl_threshold',
        plots: { ...state.plots, ...action.plots },
        stlCoverage: action.stlCoverage ?? null,
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    case 'APPLYING_STL':
      return { ...state, status: 'applying_stl' };
    case 'AWAITING_LSTM':
      return {
        ...state,
        status: 'awaiting_lstm_threshold',
        plots: { ...state.plots, ...action.plots },
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    case 'APPLYING_LSTM':
      return { ...state, status: 'applying_lstm' };
    case 'COMPLETE':
      return {
        ...state,
        status: 'complete',
        plots: { ...state.plots, ...action.plots },
        processedCsvFilename: action.processedCsvFilename,
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    case 'PIPELINE_ERROR':
      return { ...state, status: 'error', ...applyErrorPayload(action.error) };
    case 'THRESHOLD_VALIDATION_FAILED':
      // Stay in the awaiting_X state so the modal remains open for retry.
      // If we somehow drift out (defensive), bail to error.
      if (
        state.status !== 'awaiting_stl_threshold' &&
        state.status !== 'awaiting_lstm_threshold' &&
        state.status !== 'applying_stl' &&
        state.status !== 'applying_lstm'
      ) {
        return { ...state, status: 'error', ...applyErrorPayload(action.error) };
      }
      // Revert applying_X back to awaiting_X if mid-submit.
      return {
        ...state,
        status: state.status === 'applying_stl' ? 'awaiting_stl_threshold'
          : state.status === 'applying_lstm' ? 'awaiting_lstm_threshold'
          : state.status,
        ...applyErrorPayload(action.error),
      };
    case 'CANCELLED':
      // Tear down modals without painting an "error". Pipeline_status on
      // backend stays in awaiting_X until TTL eviction; user must restart
      // to begin a new run (documented in the runner).
      return {
        ...state,
        status: 'configuring',
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    case 'USE_PROCESSED':
      // Stays in `complete` until LOADED action arrives
      return state;
    case 'DISMISS_ERROR': {
      // Closing the error banner (manual OR 5s auto-dismiss) must NOT tear the
      // whole session down to the upload step — that threw away an uploaded,
      // parsed dataset on every transient error. Clear the error and return to
      // a usable state: `configuring` when a dataset is loaded (so the user can
      // adjust params and retry), else `idle` (nothing was loaded yet).
      if (state.status !== 'error') return state;
      return {
        ...state,
        status: state.columnName !== null ? 'configuring' : 'idle',
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
    }
    case 'RESET':
      return initialAnomalyState;
    default:
      return state;
  }
}

export function anomalyReducer(state: AnomalyState, action: AnomalyAction): AnomalyState {
  const next = anomalyReducerInner(state, action);
  log.fsm(action.type, state.status, next.status, action);
  return next;
}
